/* eslint-disable react/prop-types */
import React, { useMemo, useCallback, useState } from 'react'
import PropTypes from 'prop-types'
import { compose } from 'redux'
import { connect } from 'react-redux'
import { Field, formValueSelector } from 'redux-form'
import { lpForm } from 'lp-form'
import {
  ReadOnlyCellInput,
  NestedAttributesFieldArray,
  SectionHeader,
} from 'components'
import { noop, groupBy, size, get } from 'lodash'
import * as Types from 'types'
import { pluralize } from 'utils'
import {
  AddSchoolPartnershipModal,
  CopySchoolPartnershipModal,
} from '../components'
import * as flashActions from 'redux-flash'
import { SUCCESS_FLASH_MODAL_CLOSE_DELAY } from 'config'
import LoadingSpinner from 'components/loading-spinner'
import LoadingContainer from 'components/loading-container'

const propTypes = {
  change: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  error: PropTypes.string,
  flashSuccessMessage: PropTypes.func.isRequired,
  previousPartnerships: PropTypes.arrayOf(Types.partnership),
  schoolOptions: PropTypes.arrayOf(Types.searchSuggestion),
  partnerships: PropTypes.arrayOf(Types.partnerPartnershipType),
  schoolYears: PropTypes.arrayOf(Types.schoolYear).isRequired,
  submitting: PropTypes.bool.isRequired,
  submitFailed: PropTypes.bool.isRequired,
  visibleSchoolYearId: PropTypes.number.isRequired,
  updateArtist: PropTypes.func.isRequired,
  artistId: PropTypes.number.isRequired,
  setShowAddSchoolPartnershipModal: PropTypes.func.isRequired,
  showAddSchoolPartnershipModal: PropTypes.bool.isRequired,
}

const defaultProps = {
  error: '',
  partnerships: [],
  disabled: false,
  previousPartnerships: [],
}

function useActivePartnerCallback(schoolOptions) {
  const activePartnerMap = useMemo(
    () => groupBy(schoolOptions, 'id'),
    [schoolOptions]
  )
  return useCallback(
    (partnerId) => {
      return !!activePartnerMap[partnerId]
    },
    [activePartnerMap]
  )
}

function SchoolRow({ fields, index, handleSubmit, setLoading }) {
  const field = fields.name ? `${fields.name}[${index}]` : `${index}`
  const school = fields.get(index)

  const handleRemove = () => {
    setLoading(true)
    try {
      fields.destroy(index)

      // Submit the form to persist changes
      setTimeout(() => {
        handleSubmit()
        setLoading(false)
      }, 0)
    } catch (error) {
      setLoading(false)
    }
  }

  return (
    <React.Fragment>
      <tr role="group" aria-label={`School ${school.schoolName}`}>
        <th scope="row" className="table-name">
          <div className="table-name-wrapper">
            <button
              type="button"
              className="remove-row action-button"
              onClick={handleRemove}
              aria-label={`Remove ${school.schoolName || 'school'} partnership`}
            >
              <span aria-hidden="true">×</span>
            </button>
            <Field
              name={field + '.schoolName'}
              label={false}
              component={ReadOnlyCellInput}
            />
            {school.state === 'pending_approval' && (
              <div className="pending-approval">Pending</div>
            )}
          </div>
        </th>
      </tr>
    </React.Fragment>
  )
}

function SchoolFields({
  fields,
  handleSubmit,
  setLoading,
  change,
  partnerships,
}) {
  return (
    <tbody>
      {fields.map((_, i) => {
        return (
          <SchoolRow
            key={`school-${i}`}
            index={i}
            fields={fields}
            handleSubmit={handleSubmit}
            setLoading={setLoading}
            change={change}
            partnerships={partnerships}
          />
        )
      })}
    </tbody>
  )
}

function SchoolPartnershipTableForm({
  change,
  disabled,
  error,
  flashSuccessMessage,
  handleSubmit,
  schoolYears,
  visibleSchoolYearId,
  partnerships,
  schoolOptions,
  previousPartnerships,
  submitFailed,
  setShowAddSchoolPartnershipModal,
  showAddSchoolPartnershipModal,
}) {
  const isActivePartner = useActivePartnerCallback(schoolOptions)
  const [loading, setLoading] = useState(false)

  const visibleYear = useMemo(() => {
    return schoolYears.find(({ id }) => id === visibleSchoolYearId)
  }, [schoolYears, visibleSchoolYearId])

  const remainingPreviousPartnerships = useMemo(() => {
    const eligiblePartnerships = previousPartnerships.filter((partnership) => {
      return (
        isActivePartner(partnership.schoolId) &&
        !partnerships.some(({ schoolId }) => schoolId === partnership.schoolId)
      )
    })

    return eligiblePartnerships
  }, [previousPartnerships.length, partnerships.length, isActivePartner])

  const remainingSchoolOptions = useMemo(() => {
    return schoolOptions.filter((school) => {
      return !partnerships.some(({ schoolId }) => schoolId === school.id)
    })
  }, [schoolOptions, partnerships])

  const [showCopyPartnershipModal, setShowCopyPartnershipModal] =
    useState(false)

  return (
    <>
      {partnerships.length > 0 && (
        <SectionHeader>
          <h2>What schools do you work with?</h2>
          {!disabled && (
            <div className="button-area many-buttons">
              <button
                type="button"
                onClick={() => setShowAddSchoolPartnershipModal(true)}
                className="button-primary-outline"
              >
                Add a new School
              </button>
              <button
                type="button"
                onClick={() => setShowCopyPartnershipModal(true)}
                className="button-primary-outline"
              >
                Copy School from other school years
              </button>
            </div>
          )}

          <div className="portal partner-artists">
            <LoadingContainer isLoading={loading}>
              {loading && <LoadingSpinner />}
              {partnerships && partnerships.length > 0 ? (
                <form
                  onSubmit={disabled ? noop : handleSubmit}
                  disabled={disabled}
                >
                  <table>
                    <thead>
                      <tr>
                        <th scope="col">School Name</th>
                      </tr>
                    </thead>
                    <NestedAttributesFieldArray
                      name="partnerships"
                      component={SchoolFields}
                      handleSubmit={handleSubmit}
                      setLoading={setLoading}
                      disabled={disabled}
                      change={change}
                      partnerships={partnerships}
                    />
                  </table>
                  {submitFailed && error && (
                    <div className="partnership-table-errors">
                      <p className="error-message">{error}</p>
                    </div>
                  )}
                </form>
              ) : (
                <p>
                  No partners reported for the{' '}
                  {get(visibleYear, 'number', 'selected')} school year.
                </p>
              )}
            </LoadingContainer>
          </div>
        </SectionHeader>
      )}
      {showAddSchoolPartnershipModal && (
        <AddSchoolPartnershipModal
          onClose={() => setShowAddSchoolPartnershipModal(false)}
          onSubmit={async ({ schools }) => {
            setLoading(true)

            const schoolPartnerships = schools.map((school) => ({
              schoolId: school.id,
              schoolName: school.name,
            }))

            change('partnerships', [...partnerships, ...schoolPartnerships])

            // Submit the form after a brief delay to ensure state is updated
            setTimeout(() => {
              handleSubmit()
              setLoading(false)
            }, 0)
            return schools.length
          }}
          onSubmitSuccess={(count) => {
            setShowAddSchoolPartnershipModal(false)
            setTimeout(
              () =>
                flashSuccessMessage(
                  `${count} ${pluralize('partnership', count)} added for the ${
                    visibleYear.number
                  } school year`
                ),
              SUCCESS_FLASH_MODAL_CLOSE_DELAY
            )
          }}
          schoolOptions={remainingSchoolOptions}
        />
      )}

      {showCopyPartnershipModal && (
        <CopySchoolPartnershipModal
          onClose={() => setShowCopyPartnershipModal(false)}
          onSubmit={async ({ partnerships: newPartnerships }) => {
            setLoading(true)
            change('partnerships', [...partnerships, ...newPartnerships])
            // Submit the form after a brief delay to ensure state is updated
            setTimeout(() => {
              handleSubmit()
              setLoading(false)
            }, 0)
            return newPartnerships
          }}
          onSubmitSuccess={(partnerships) => {
            setShowCopyPartnershipModal(false)
            const numNewPartnerships = size(partnerships)
            setTimeout(
              () =>
                flashSuccessMessage(
                  `${numNewPartnerships} ${pluralize(
                    'partnership',
                    numNewPartnerships
                  )} copied over for the ${visibleYear.number} school year`
                ),
              SUCCESS_FLASH_MODAL_CLOSE_DELAY
            )
          }}
          partnerships={remainingPreviousPartnerships}
          schoolYears={schoolYears}
        />
      )}
    </>
  )
}

SchoolPartnershipTableForm.propTypes = propTypes
SchoolPartnershipTableForm.defaultProps = defaultProps

function mapStateToProps(state, { name }) {
  return {
    partnerships: formValueSelector(name)(state, 'partnerships'),
  }
}

const mapDispatchToProps = {
  flashSuccessMessage: flashActions.flashSuccessMessage,
}

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  lpForm({
    submitFilters: {
      allow: ['id', 'partnerships'],
    },
    enableReinitialize: true,
    keepDirtyOnReinitialize: true,
    destroyOnUnmount: false, // allow switching between year tabs without losing in-progress data
  })
)(SchoolPartnershipTableForm)
